import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Container, Typography, Box, Grid, Paper, CircularProgress } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import Map from '../components/Map';
import KmlMap from '../components/KmlMap';
import CustomTooltip from '../components/CustomTooltip';
import config from '../config/config'; 

const COLORS = ['#FFBB28', '#00C49F', '#FF8042', '#FFBB28', '#00C49F', '#FF8042'];

const Dashboard = () => {
  const [projects, setProjects] = useState([]);
  const [statusDetails, setStatusDetails] = useState([]);
  const [delayedProjects, setDeleyedProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const [projectsResponse, statusDetailsResponse,delayedProjects] = await Promise.all([
          axios.get(`${config.apiBaseUrl}/api/projects`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-TenantID': 'admin',
            },
          }),
          axios.get(`${config.apiBaseUrl}/api/projects/statusdetails`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-TenantID': 'admin',
            },
          }),
          axios.get(`${config.apiBaseUrl}/api/projects/delayedprojects`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'X-TenantID': 'admin',
            },
          }),
        ]);
        setProjects(projectsResponse.data);
        setStatusDetails(statusDetailsResponse.data);
        setDeleyedProjects(delayedProjects.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);



  const costData = projects
    .filter((project) => project.status === 'Completed')
    .map((project) => ({
      name: project.project_name,
      estimated_cost: Number(project.estimated_cost) || 0,
      actual_cost: Number(project.actual_cost) || 0,
    }));

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h5" color='#0099cc' gutterBottom>
        Project Analytics Dashboard
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ padding: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Project Status Distribution
            </Typography>
            <PieChart width={300} height={300}>
              <Pie
                data={statusDetails.filter(d => !d.parent)}
                cx={150}
                cy={150}
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {statusDetails.filter(d => !d.parent).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Pie
                data={statusDetails.filter(d => !d.parent)}
                cx={150}
                cy={150}
                labelLine={false}
                innerRadius={90}
                outerRadius={120}
                fill="#82ca9d"
                dataKey="value"
              >
                {statusDetails.filter(d => !d.parent).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
        <Paper elevation={3} sx={{ padding: 2, borderRadius: 2 }}>
  <Typography variant="h6" gutterBottom>
    Delay in Documents Submission
  </Typography>
  <BarChart
    width={300}
    height={300}
    data={delayedProjects}
    margin={{ top: 5, right: 30, left: 20, bottom: 50 }}
  >
    <CartesianGrid strokeDasharray="3 3" />
    <XAxis
      dataKey="noOfDaysDelayed"
      angle={0}
      textAnchor="end"
      interval={0}
      label={{
        value: 'Days Delayed',
        position: 'bottom',
        style: { fontSize: '12px' }, 
        offset: 10, 
      }}
    />
   <YAxis
  label={{
    value: 'Count of Projects',
    angle: -90, 
    position: 'outsideLeft', 
    offset: -15, 
    style: { fontSize: '12px' }, 
  }}
/>
    <Tooltip content={<CustomTooltip />} />
    <Legend />
    <Bar dataKey="countOfProjectsDelayed" fill="#8884d8" legendType="none" label={false} />
  </BarChart>
</Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ padding: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Estimated vs Actual Cost
            </Typography>
            <BarChart
              width={300}
              height={300}
              data={costData}
              margin={{
                top: 5, right: 30, left: 20, bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={false} />
              <YAxis domain={[0, (dataMax) => Math.ceil(dataMax)]} />
              <Tooltip formatter={(value) => new Intl.NumberFormat('en').format(value)} />
              <Legend />
              <Bar dataKey="estimated_cost" fill="#8884d8" name="Estimated Cost" />
              <Bar dataKey="actual_cost" fill="#82ca9d" name="Actual Cost" />
            </BarChart>
          </Paper>
        </Grid>
      </Grid>
      <Box mt={3}>
        <Paper elevation={3} sx={{ padding: 2, borderRadius: 2 }}>
          <Map projects={projects} />
        </Paper>
      </Box>
    
    </Container>
  );
};

export default Dashboard;